// Serviço CRUD para AllergyIntolerance com integração PQC
import { supabase } from './supabase'
import { medicalPQC } from './medicalPQCIntegration'
import { MedicalRecordsSystem, EnhancedMedicalRecord } from './medicalRecordsSystem'
import {
  FHIRAllergyIntolerance,
  AllergyIntoleranceMapping
} from '../types/fhir-allergy-intolerance'
import {
  convertToFHIR,
  convertFromFHIR,
  validateFHIRRecord,
  createDefaultFHIRRecord
} from './fhir-allergy-mapping'
import { toast } from 'sonner'

export interface AllergyIntoleranceCreateData {
  patient_id: string
  doctor_id: string
  title: string
  description?: string
  // Campos obrigatórios para validação
  clinical_status: string
  verification_status: string
  substance_display: string
  // Campos opcionais específicos
  allergy_type?: string
  allergy_categories?: string[]
  criticality?: string
  substance_code?: string
  onset?: string
  recorded_date?: string
  reactions?: any[]
  notes?: string[]
  // Campos customizados adicionais
  custom_fields?: Record<string, any>
}

export interface AllergyIntoleranceUpdateData extends Partial<AllergyIntoleranceCreateData> {
  id: string
}

export interface AllergyIntoleranceSearchFilters {
  patient_id?: string
  doctor_id?: string
  clinical_status?: string
  verification_status?: string
  type?: string
  category?: string
  criticality?: string
  substance?: string
  date_from?: string
  date_to?: string
}

export interface AllergyIntoleranceListResponse {
  data: EnhancedMedicalRecord[]
  count: number
  error?: any
}

export class AllergyIntoleranceService {
  // Criar nova alergia/intolerância - MODO SIMPLIFICADO SEM CRIPTOGRAFIA
  static async create(data: AllergyIntoleranceCreateData): Promise<{ data: EnhancedMedicalRecord | null; error: any }> {
    try {
      console.log('🔧 MODO SIMPLIFICADO: Salvando sem criptografia PQC devido a problemas RLS')
      
      // Validar dados de entrada
      if (!data.patient_id || !data.doctor_id || !data.title) {
        return {
          data: null,
          error: { message: 'Campos obrigatórios ausentes: patient_id, doctor_id, title' }
        }
      }

      // Preparar dados estruturados para o campo description
      const structuredData = {
        clinical_status: data.clinical_status,
        verification_status: data.verification_status,
        substance_display: data.substance_display,
        allergy_type: data.allergy_type,
        allergy_categories: data.allergy_categories,
        criticality: data.criticality,
        custom_fields: data.custom_fields,
        mode: 'simplified_no_encryption',
        timestamp: new Date().toISOString()
      }

      // Preparar dados para inserção - APENAS CAMPOS BÁSICOS
      const insertData = {
        patient_id: data.patient_id,
        doctor_id: data.doctor_id,
        record_type: 'allergy_intolerance',
        title: data.title,
        description: JSON.stringify(structuredData),
        is_encrypted: false
      }

      console.log('📝 Inserindo registro simplificado:', insertData)

      // Inserir no banco de dados - SEM CRIPTOGRAFIA
      const { data: insertedRecord, error: insertError } = await supabase
        .from('medical_records')
        .insert(insertData)
        .select('*')
        .single()

      if (insertError) {
        console.error('Erro ao inserir registro:', insertError)
        return { data: null, error: insertError }
      }

      console.log('✅ Registro salvo com sucesso (modo simplificado):', insertedRecord)

      // Retornar registro completo
      const completeRecord: EnhancedMedicalRecord = {
        ...insertedRecord,
        clinical_status: data.clinical_status,
        verification_status: data.verification_status,
        substance_display: data.substance_display,
        allergy_type: data.allergy_type,
        allergy_categories: data.allergy_categories,
        criticality: data.criticality,
        custom_fields: data.custom_fields
      } as EnhancedMedicalRecord

      toast.success('Alergia/Intolerância criada com sucesso (modo simplificado)')
      return { data: completeRecord, error: null }

    } catch (error) {
      console.error('Erro ao criar alergia/intolerância:', error)
      return {
        data: null,
        error: { message: 'Erro interno do servidor' }
      }
    }
  }

  // Buscar alergia/intolerância por ID
  static async getById(id: string, requesterId: string): Promise<{ data: EnhancedMedicalRecord | null; error: any }> {
    try {
      console.log('🔍 [DEBUG] Buscando registro por ID:', id, 'Requester:', requesterId)
      
      // Buscar registro no banco
      const { data: record, error: fetchError } = await supabase
        .from('medical_records')
        .select('*')
        .eq('id', id)
        .eq('record_type', 'allergy_intolerance')
        .single()

      if (fetchError) {
        console.error('❌ [DEBUG] Erro ao buscar registro:', fetchError)
        return { data: null, error: fetchError }
      }

      if (!record) {
        console.error('❌ [DEBUG] Registro não encontrado')
        return { data: null, error: { message: 'Registro não encontrado' } }
      }

      console.log('📋 [DEBUG] Registro encontrado:', record)

      // Verificar se é modo simplificado (sem criptografia)
      if (!record.is_encrypted) {
        console.log('🔓 [DEBUG] Modo simplificado - extraindo dados do campo description')
        
        let structuredData = {}
        let descriptionText = record.description || ''
        
        // Tentar extrair dados estruturados do campo description
        if (record.description) {
          try {
            const parsedDescription = JSON.parse(record.description)
            if (typeof parsedDescription === 'object' && parsedDescription !== null) {
              structuredData = parsedDescription
              descriptionText = parsedDescription.description || ''
              console.log('📊 [DEBUG] Dados estruturados extraídos:', structuredData)
            }
          } catch (e) {
            console.log('📝 [DEBUG] Description não é JSON, usando como texto simples')
          }
        }

        // Montar registro completo com dados estruturados
        const completeRecord: EnhancedMedicalRecord = {
          ...record,
          description: descriptionText,
          custom_fields: {
            ...structuredData,
            ...(record.custom_fields || {})
          },
          version: 1,
          tags: ['fhir', 'allergy', 'intolerance'],
          priority: record.priority === 'critical' ? 'high' : 'normal',
          status: 'active'
        }

        console.log('✅ [DEBUG] Registro completo montado (modo simplificado):', completeRecord)
        return { data: completeRecord, error: null }
      }

      // Modo criptografado - tentar descriptografar
      console.log('🔐 [DEBUG] Modo criptografado - tentando descriptografar')
      const decryptedData = await medicalPQC.decryptMedicalRecord(
        {
          encrypted_content: record.encrypted_data,
          kyber_public_key: '', // Será obtido da chave PQC
          dilithium_signature: '', // Será obtido da chave PQC
          encryption_metadata: {
            algorithm: 'kyber',
            key_size: 1024,
            timestamp: record.created_at || new Date().toISOString(),
            version: '1.0'
          }
        },
        record.patient_id,
        requesterId
      )

      if (!decryptedData) {
        console.error('❌ [DEBUG] Falha na descriptografia ou acesso negado')
        return {
          data: null,
          error: { message: 'Falha na descriptografia ou acesso negado' }
        }
      }

      // Montar registro completo
      const completeRecord: EnhancedMedicalRecord = {
        ...record,
        description: decryptedData.description,
        custom_fields: decryptedData.custom_fields,
        version: 1,
        tags: ['fhir', 'allergy', 'intolerance'],
        priority: record.priority === 'critical' ? 'high' : 'normal',
        status: 'active'
      }

      console.log('✅ [DEBUG] Registro completo montado (modo criptografado):', completeRecord)
      return { data: completeRecord, error: null }

    } catch (error) {
      console.error('💥 [DEBUG] Erro crítico ao buscar alergia/intolerância:', error)
      return {
        data: null,
        error: { message: 'Erro interno do servidor' }
      }
    }
  }

  // Listar alergias/intolerâncias com filtros
  static async list(
    filters: AllergyIntoleranceSearchFilters,
    requesterId: string,
    page: number = 1,
    limit: number = 20
  ): Promise<AllergyIntoleranceListResponse> {
    try {
      let query = supabase
        .from('medical_records')
        .select('*', { count: 'exact' })
        .eq('record_type', 'allergy_intolerance')
        .order('created_at', { ascending: false })

      // Aplicar filtros
      if (filters.patient_id) {
        query = query.eq('patient_id', filters.patient_id)
      }
      if (filters.doctor_id) {
        query = query.eq('doctor_id', filters.doctor_id)
      }
      if (filters.date_from) {
        query = query.gte('created_at', filters.date_from)
      }
      if (filters.date_to) {
        query = query.lte('created_at', filters.date_to)
      }

      // Paginação
      const offset = (page - 1) * limit
      query = query.range(offset, offset + limit - 1)

      const { data: records, error: fetchError, count } = await query

      if (fetchError) {
        return { data: [], count: 0, error: fetchError }
      }

      // Descriptografar registros (apenas metadados para listagem)
      const processedRecords: EnhancedMedicalRecord[] = []
      
      for (const record of records || []) {
        try {
          // Para listagem, só descriptografamos se o usuário tem acesso
          const hasAccess = record.patient_id === requesterId || record.doctor_id === requesterId
          
          if (hasAccess) {
            const decryptedData = await medicalPQC.decryptMedicalRecord(
              {
                encrypted_content: record.encrypted_data,
                kyber_public_key: '', // Será obtido da chave PQC
                dilithium_signature: '', // Será obtido da chave PQC
                encryption_metadata: {
                  algorithm: 'kyber',
                  key_size: 1024,
                  timestamp: record.created_at || new Date().toISOString(),
                  version: '1.0'
                }
              },
              record.patient_id,
              requesterId,
              record.id
            )

            if (decryptedData) {
              // Aplicar filtros nos dados descriptografados
              const customFields = decryptedData.custom_fields || {}
              
              if (filters.clinical_status && customFields.clinical_status !== filters.clinical_status) continue
              if (filters.verification_status && customFields.verification_status !== filters.verification_status) continue
              if (filters.type && customFields.type !== filters.type) continue
              if (filters.criticality && customFields.criticality !== filters.criticality) continue
              if (filters.substance && !customFields.substance_display?.toLowerCase().includes(filters.substance.toLowerCase())) continue
              if (filters.category && !customFields.category?.includes(filters.category)) continue

              processedRecords.push({
                ...record,
                description: decryptedData.description,
                custom_fields: customFields,
                version: 1,
                tags: ['fhir', 'allergy', 'intolerance'],
                priority: record.priority === 'critical' ? 'high' : 'normal',
                status: 'active'
              })
            }
          } else {
            // Para registros sem acesso, mostrar apenas metadados básicos
            processedRecords.push({
              ...record,
              description: 'Acesso restrito',
              custom_fields: {},
              version: 1,
              tags: ['fhir', 'allergy', 'intolerance'],
              priority: 'normal',
              status: 'active'
            })
          }
        } catch (error) {
          console.error('Erro ao processar registro:', record.id, error)
          // Continuar com próximo registro
        }
      }

      return {
        data: processedRecords,
        count: count || 0,
        error: null
      }

    } catch (error) {
      console.error('Erro ao listar alergias/intolerâncias:', error)
      return {
        data: [],
        count: 0,
        error: { message: 'Erro interno do servidor' }
      }
    }
  }

  // Atualizar alergia/intolerância com versionamento
  static async update(data: AllergyIntoleranceUpdateData, requesterId: string): Promise<{ data: EnhancedMedicalRecord | null; error: any }> {
    try {
      console.log('🔄 Iniciando atualização com versionamento para registro:', data.id)
      
      // Buscar registro existente
      const { data: existingRecord, error: fetchError } = await this.getById(data.id, requesterId)
      
      if (fetchError || !existingRecord) {
        return { data: null, error: fetchError || { message: 'Registro não encontrado' } }
      }

      // Verificar se é a versão atual
      if (!existingRecord.is_current_version) {
        return { 
          data: null, 
          error: { message: 'Apenas a versão atual pode ser editada. Esta é uma versão histórica.' } 
        }
      }

      // Verificar permissões
      if (existingRecord.doctor_id !== requesterId && existingRecord.patient_id !== requesterId) {
        return { data: null, error: { message: 'Acesso negado' } }
      }

      // Buscar versão máxima da cadeia para incrementar corretamente
      const { data: maxVersionData, error: versionError } = await supabase
        .rpc('get_next_version', { record_id: data.id })
      
      if (versionError) {
        console.error('Erro ao buscar próxima versão:', versionError)
        return { data: null, error: versionError }
      }

      const newVersion = maxVersionData || ((existingRecord.version || 1) + 1)
      console.log('📊 Nova versão será:', newVersion, 'baseada na versão atual:', existingRecord.version)
      const newVersionData = {
        patient_id: existingRecord.patient_id,
        doctor_id: existingRecord.doctor_id,
        record_type: existingRecord.record_type,
        title: data.title || existingRecord.title,
        description: data.description || existingRecord.description,
        version: newVersion,
        previous_version_id: existingRecord.id,
        is_current_version: true,
        is_encrypted: false // Modo simplificado
      }

      // Validar dados da nova versão
      const category = MedicalRecordsSystem.getCategory('allergy_intolerance')
      const validation = MedicalRecordsSystem.validateRecord(newVersionData, category)
      
      if (!validation.isValid) {
        return {
          data: null,
          error: { message: `Erros de validação: ${validation.errors.join(', ')}` }
        }
      }

      // Converter para FHIR e validar
      const mergedData = { ...existingRecord, ...data }
      const fhirRecord = convertToFHIR(mergedData as EnhancedMedicalRecord)
      const fhirValidation = validateFHIRRecord(fhirRecord)
      
      if (!fhirValidation.isValid) {
        return {
          data: null,
          error: { message: `Erros FHIR: ${fhirValidation.errors.join(', ')}` }
        }
      }

      console.log('🔧 MODO SIMPLIFICADO: Criando nova versão sem criptografia PQC')
      
      // Estruturar dados FHIR no campo description
      const structuredData = {
        fhir_record: fhirRecord,
        clinical_status: data.clinical_status || existingRecord.custom_fields?.clinical_status,
        substance_display: data.substance_display || existingRecord.custom_fields?.substance_display,
        custom_fields: {
          ...existingRecord.custom_fields,
          ...data.custom_fields
        }
      }

      // 1. Marcar versão atual como não atual
      const { error: markOldError } = await supabase
        .from('medical_records')
        .update({ is_current_version: false })
        .eq('id', existingRecord.id)

      if (markOldError) {
        console.error('Erro ao marcar versão anterior:', markOldError)
        return { data: null, error: markOldError }
      }

      // 2. Criar nova versão
      const { data: newRecord, error: createError } = await supabase
        .from('medical_records')
        .insert({
          ...newVersionData,
          description: JSON.stringify(structuredData)
        })
        .select('*')
        .single()

      if (createError) {
        console.error('Erro ao criar nova versão:', createError)
        // Reverter marcação da versão anterior
        await supabase
          .from('medical_records')
          .update({ is_current_version: true })
          .eq('id', existingRecord.id)
        return { data: null, error: createError }
      }

      console.log('✅ Nova versão criada com sucesso:', {
        id: newRecord.id,
        version: newRecord.version,
        previous_version_id: newRecord.previous_version_id
      })

      // Retornar registro completo
      const completeRecord: EnhancedMedicalRecord = {
        ...newRecord,
        custom_fields: structuredData.custom_fields,
        tags: ['fhir', 'allergy', 'intolerance'],
        priority: newRecord.priority || 'normal',
        status: 'active'
      }

      toast.success(`Alergia/Intolerância atualizada (versão ${newVersion})`)
      return { data: completeRecord, error: null }

    } catch (error) {
      console.error('Erro ao atualizar alergia/intolerância:', error)
      return {
        data: null,
        error: { message: 'Erro interno do servidor' }
      }
    }
  }

  // Deletar alergia/intolerância
  static async delete(id: string, requesterId: string): Promise<{ success: boolean; error: any }> {
    try {
      // Verificar se existe e se tem permissão
      const { data: record, error: fetchError } = await this.getById(id, requesterId)
      
      if (fetchError || !record) {
        return { success: false, error: fetchError || { message: 'Registro não encontrado' } }
      }

      if (record.doctor_id !== requesterId) {
        return { success: false, error: { message: 'Apenas o médico que criou pode deletar' } }
      }

      // Soft delete - marcar como deletado
      const { error: deleteError } = await supabase
        .from('medical_records')
        .update({ 
          status: 'deleted',
          updated_at: new Date().toISOString()
        })
        .eq('id', id)

      if (deleteError) {
        return { success: false, error: deleteError }
      }

      toast.success('Alergia/Intolerância removida com sucesso')
      return { success: true, error: null }

    } catch (error) {
      console.error('Erro ao deletar alergia/intolerância:', error)
      return {
        success: false,
        error: { message: 'Erro interno do servidor' }
      }
    }
  }

  // Buscar por paciente
  static async getByPatient(patientId: string, requesterId: string): Promise<AllergyIntoleranceListResponse> {
    return this.list({ patient_id: patientId }, requesterId)
  }

  // Buscar por médico
  static async getByDoctor(doctorId: string, requesterId: string): Promise<AllergyIntoleranceListResponse> {
    return this.list({ doctor_id: doctorId }, requesterId)
  }

  // Obter navegação entre versões (anterior/próxima)
  static async getVersionNavigation(recordId: string, requesterId: string): Promise<{ data: { previous: string | null; next: string | null; current: number; total: number } | null; error: any }> {
    try {
      console.log('🧭 Buscando navegação de versões para registro:', recordId)
      
      // Buscar registro atual
      const { data: currentRecord, error: currentError } = await supabase
        .from('medical_records')
        .select('id, version, previous_version_id, is_current_version')
        .eq('id', recordId)
        .single()

      if (currentError || !currentRecord) {
        return { data: null, error: currentError || { message: 'Registro não encontrado' } }
      }

      // Buscar todas as versões da cadeia usando função recursiva
      const { data: allVersions, error: versionsError } = await supabase
        .from('medical_records')
        .select('id, version, previous_version_id, is_current_version')
        .eq('record_type', 'allergy_intolerance')
        .order('version', { ascending: true })

      if (versionsError) {
        return { data: null, error: versionsError }
      }

      // Filtrar versões da mesma cadeia
      const chainVersions = allVersions.filter(v => {
        // Incluir se é o registro atual
        if (v.id === recordId) return true
        // Incluir se aponta para o registro atual como versão anterior
        if (v.previous_version_id === recordId) return true
        // Incluir se o registro atual aponta para ele como versão anterior
        if (currentRecord.previous_version_id === v.id) return true
        return false
      }).sort((a, b) => a.version - b.version)

      const currentIndex = chainVersions.findIndex(v => v.id === recordId)
      const previousVersion = currentIndex > 0 ? chainVersions[currentIndex - 1] : null
      const nextVersion = currentIndex < chainVersions.length - 1 ? chainVersions[currentIndex + 1] : null

      return {
        data: {
          previous: previousVersion?.id || null,
          next: nextVersion?.id || null,
          current: currentRecord.version,
          total: chainVersions.length
        },
        error: null
      }
    } catch (error) {
      console.error('Erro ao buscar navegação de versões:', error)
      return { data: null, error: { message: 'Erro interno do servidor' } }
    }
  }

  // Obter histórico de versões de um registro
  static async getVersionHistory(recordId: string, requesterId: string): Promise<{ data: EnhancedMedicalRecord[]; error: any }> {
    try {
      console.log('📋 Buscando histórico de versões para registro:', recordId)
      
      // Buscar todas as versões relacionadas (atual e anteriores)
      const { data: versions, error: fetchError } = await supabase
        .from('medical_records')
        .select('*')
        .or(`id.eq.${recordId},previous_version_id.eq.${recordId}`)
        .eq('record_type', 'allergy_intolerance')
        .order('version', { ascending: false })

      if (fetchError) {
        console.error('Erro ao buscar histórico de versões:', fetchError)
        return { data: [], error: fetchError }
      }

      if (!versions || versions.length === 0) {
        return { data: [], error: { message: 'Nenhuma versão encontrada' } }
      }

      // Verificar permissões no primeiro registro
      const firstRecord = versions[0]
      if (firstRecord.doctor_id !== requesterId && firstRecord.patient_id !== requesterId) {
        return { data: [], error: { message: 'Acesso negado' } }
      }

      // Processar registros para incluir dados estruturados
      const processedVersions: EnhancedMedicalRecord[] = []
      
      for (const record of versions) {
        try {
          let customFields = {}
          let description = record.description
          
          // Tentar parsear dados estruturados do campo description
          if (record.description && record.description.startsWith('{')) {
            try {
              const structuredData = JSON.parse(record.description)
              customFields = structuredData.custom_fields || {}
              description = structuredData.fhir_record?.code?.text || record.title
            } catch (parseError) {
              console.warn('Erro ao parsear dados estruturados:', parseError)
            }
          }

          processedVersions.push({
            ...record,
            description,
            custom_fields: customFields,
            tags: ['fhir', 'allergy', 'intolerance'],
            priority: record.priority || 'normal',
            status: record.is_current_version ? 'active' : 'archived'
          })
        } catch (error) {
          console.error('Erro ao processar versão:', error)
          // Incluir registro mesmo com erro de processamento
          processedVersions.push({
            ...record,
            custom_fields: {},
            tags: ['fhir', 'allergy', 'intolerance'],
            priority: 'normal',
            status: record.is_current_version ? 'active' : 'archived'
          })
        }
      }

      console.log(`✅ Histórico de versões carregado: ${processedVersions.length} versões`)
      return { data: processedVersions, error: null }

    } catch (error) {
      console.error('Erro ao buscar histórico de versões:', error)
      return {
        data: [],
        error: { message: 'Erro interno do servidor' }
      }
    }
  }

  // Estatísticas
  static async getStatistics(filters: AllergyIntoleranceSearchFilters, requesterId: string) {
    try {
      const { data: records } = await this.list(filters, requesterId, 1, 1000)
      
      const stats = {
        total: records.length,
        by_clinical_status: {} as Record<string, number>,
        by_type: {} as Record<string, number>,
        by_category: {} as Record<string, number>,
        by_criticality: {} as Record<string, number>,
        by_month: {} as Record<string, number>
      }

      records.forEach(record => {
        const customFields = record.custom_fields || {}
        
        // Por status clínico
        const clinicalStatus = customFields.clinical_status || 'unknown'
        stats.by_clinical_status[clinicalStatus] = (stats.by_clinical_status[clinicalStatus] || 0) + 1
        
        // Por tipo
        const type = customFields.type || 'unknown'
        stats.by_type[type] = (stats.by_type[type] || 0) + 1
        
        // Por criticidade
        const criticality = customFields.criticality || 'unknown'
        stats.by_criticality[criticality] = (stats.by_criticality[criticality] || 0) + 1
        
        // Por categoria
        const categories = customFields.category || []
        categories.forEach((cat: string) => {
          stats.by_category[cat] = (stats.by_category[cat] || 0) + 1
        })
        
        // Por mês
        const month = record.created_at?.substring(0, 7) || 'unknown'
        stats.by_month[month] = (stats.by_month[month] || 0) + 1
      })

      return { data: stats, error: null }
    } catch (error) {
      console.error('Erro ao gerar estatísticas:', error)
      return { data: null, error: { message: 'Erro interno do servidor' } }
    }
  }
}

export default AllergyIntoleranceService